import type { OnboardingData } from "../onboarding/types";

type JsonRecord = Record<string, unknown>;

function isJsonRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Serialize onboarding answers deterministically so the screen-level save and
 * the app-level rescue produce the same identity even when their objects were
 * assembled in a different property order.
 */
function stableSerialize(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableSerialize(entry)).join(",")}]`;
  }

  if (isJsonRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`)
      .join(",")}}`;
  }

  if (value === undefined) return "undefined";
  return JSON.stringify(value) ?? String(value);
}

export function createOnboardingSaveKey(
  userId: string,
  data: OnboardingData,
  stytchPhone: string | undefined,
): string {
  return stableSerialize({ data, stytchPhone, userId });
}

function hashSeed(seed: string, salt: number): string {
  let hash = (2166136261 ^ salt) >>> 0;
  for (const character of seed) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

/**
 * Produce a deterministic UUID-shaped replay key for the patient PATCH.
 * Keeping it derived from the save key lets a retry after a lost response
 * replay the same server operation instead of creating a second write.
 */
export function createOnboardingSaveIdempotencyKey(saveKey: string): string {
  const hex = [0, 1, 2, 3].map((salt) => hashSeed(saveKey, salt)).join("");
  const variant = (Number.parseInt(hex.slice(16, 18), 16) & 0x3f) | 0x80;
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(
    13,
    16,
  )}-${variant.toString(16).padStart(2, "0")}${hex.slice(18, 20)}-${hex.slice(
    20,
    32,
  )}`;
}

const inFlightSaves = new Map<string, Promise<void>>();
const completedSaves = new Set<string>();

/**
 * Deduplicate one onboarding payload for one authenticated user.
 *
 * The promise map handles concurrent callers. The completed set also covers a
 * late caller that loaded the same answers before the first caller cleared
 * storage. Failed attempts are removed so a user can retry.
 */
export function runOnboardingSaveOnce(
  key: string,
  run: () => Promise<void>,
): Promise<void> {
  if (completedSaves.has(key)) return Promise.resolve();

  const inFlight = inFlightSaves.get(key);
  if (inFlight) return inFlight;

  const savePromise = Promise.resolve()
    .then(run)
    .then(
      () => {
        inFlightSaves.delete(key);
        completedSaves.add(key);
      },
      (error: unknown) => {
        inFlightSaves.delete(key);
        throw error;
      },
    );

  inFlightSaves.set(key, savePromise);
  return savePromise;
}
